import { useState } from 'react'
import { Button, Typography } from '@mui/material'
import { CheckCircleRounded } from '@mui/icons-material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import PublishBomDialog from './PublishBomDialog'
import { readBomLibraryError, unlinkWorkflowBom } from '../bomLibrary/bomLibraryApi'

/**
 * PublishAfterImportDialog — S113 (HU-06/CA6.4, RN-06.8/D22): the decision the user must take right
 * after importing a BOM file over a workflow that is LINKED to a library BOM.
 *
 * The import itself is untouched (RN-06.1): it lands in the editor's temp scope exactly as it does
 * on an embedded BOM. What cannot be left undecided is what that temp load MEANS for the link:
 *
 * - **publish** → a new version of that same BOM, and the link moves to it (via `PublishBomDialog`,
 *   preloaded with the BOM's code and name and pointed at the temp scope);
 * - **keep as embedded BOM** → the link is deleted (N17, carrying the temp scope so Sun knows which
 *   import the editor is looking at) and the workflow owns its BOM again, with no trace of where it
 *   came from (D22).
 *
 * Both buttons TAKE the decision — there is no third way out, and no implicit close either: leaving
 * a temp load on a linked workflow is precisely what Sun's D18 turns into an unintended version n+1
 * on the next save (contrato-sun.md §7, deviation 9).
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {number} props.workflowId
 * @param {string} props.tempWorkflowUUID the temp scope the file was imported into
 * @param {object} props.link the `WorkflowBomLink` of the workflow before the decision
 * @param {function(object)} props.onPublished called with the `BomPublishResult`
 * @param {function()} props.onKeptEmbedded called once the link has been removed
 */
const PublishAfterImportDialog = ({
  open = false,
  workflowId = 0,
  tempWorkflowUUID = '',
  link = {},
  onPublished = () => {},
  onKeptEmbedded = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [busy, setBusy] = useState(false)

  const keepEmbedded = async () => {
    setBusy(true)
    setErrorMessage('')
    try {
      await unlinkWorkflowBom(workflowId, tempWorkflowUUID)
      onKeptEmbedded()
    } catch (thrown) {
      // The decision stays on the table: silently closing would leave the workflow linked WITH a
      // temp load, the one state this dialog exists to prevent.
      setErrorMessage(readBomLibraryError(thrown).error)
    }
    setBusy(false)
  }

  return (
    <>
      <SmartDialog
        setOpen={open && !publishDialogOpen}
        title={{ render: <Typography variant='h6'>{t_('Imported BOM on a linked workflow')}</Typography> }}
        message={t_('The imported components replace the structure of {{code}} version {{version}} in this workflow. Choose what to do with the library link.', {
          code: link.code,
          version: link.versionNumber
        })}
        // "Keep as embedded BOM" is the CANCEL slot of the shared dialog: it is the decision that
        // gives the workflow its BOM back. `autoClose` stays off because the dialog may only leave
        // the screen once N17 has actually succeeded.
        cancelLabel='Keep as embedded BOM'
        cancelCallback={keepEmbedded}
        // The other decision names the BOM it would version up, so its label is interpolated and
        // cannot travel as an i18n key through `acceptLabel` (which translates what it is given).
        accept={false}
        renderCustomButtons={
          <Button variant='outlined' onClick={() => setPublishDialogOpen(true)} disabled={busy} data-testid='publish-after-import-publish'>
            <Typography>{t_('Publish as new version of {{code}}', { code: link.code })}</Typography>
            <CheckCircleRounded color={busy ? '' : 'primary'} sx={{ fontSize: '1.4em', marginLeft: '0.3rem' }} />
          </Button>
        }
        disableButtons={busy}
        autoClose={false}
        // Neither button is a way OUT: both take the decision. Backdrop and Escape are left at the
        // `SmartDialog` defaults (`closeOnBackdrop`/`closeOnEscape` false) because leaving a temp load
        // on a linked workflow is what D18 turns into an unintended version n+1 on the next save.
        renderComponent={
          errorMessage
            ? <Typography color='error.main' data-testid='publish-after-import-error'>{t_(errorMessage)}</Typography>
            : null
        }
      />
      {/* Mounted only while open: `PublishBomDialog` seeds its code/name from props on mount, and the
          link is only known once this decision is actually being taken. */}
      {publishDialogOpen
        ? (
          <PublishBomDialog
            open
            workflowId={workflowId}
            tempWorkflowUUID={tempWorkflowUUID}
            // The structure to publish is the freshly imported one, which only lives in the temp scope.
            hasWorkflowResources
            initialCode={link.code || ''}
            initialName={link.name || ''}
            onPublished={onPublished}
            onCancel={() => setPublishDialogOpen(false)}
          />
          )
        : null}
    </>
  )
}

export default PublishAfterImportDialog
